import {
    Activity,
    AssignAmConfigPayload,
    CommandResponse,
    EntityCommandCreateResponse,
    PlacementPayload,
    RevisionedCommandResponse,
    StudyEntity,
    toActivity,
} from '@faro/study-designer-model';
import axios from 'axios';
import { ScheduleOfActivitiesQueryParameter, StudyPathParameter } from '../parameter';

export type ActivityPathParameters = StudyPathParameter;
export type ActivityQueryParameters = ScheduleOfActivitiesQueryParameter & {
    /**
     * Filters the results matching the given classes
     * This filter is mutually exclusive with {@link #assessmentMeasurementScales}
     */
    readonly includedAssessmentMeasurementClasses?: string;

    /**
     * A collection of assessment/measurement classes that should be excluded from the result set.
     */
    readonly excludedAssessmentMeasurementClasses?: string;

    /**
     * Filters the results matching the given scales
     * This filter is mutually exclusive with {@link #includedAssessmentMeasurementClasses}
     */
    readonly assessmentMeasurementScales?: string;
};

/**
 * Client responsible for accessing the backend entity services
 */
export const activityClient = {
    list(query?: ActivityPathParameters & ActivityQueryParameters, signal?: AbortSignal): Promise<Activity[]> {
        return axios
            .get(`/api/study-designer/studies/${query?.studyId}/activities`, {
                signal,
                params: query,
            })
            .then(response => response.data?.map(toActivity) ?? []);
    },
    create(entity: Activity, signal?: AbortSignal): Promise<RevisionedCommandResponse<EntityCommandCreateResponse>> {
        return axios
            .post(`/api/study-designer/studies/${entity.studyId}/activities`, entity, {
                signal,
            })
            .then(response => response.data);
    },
    update(entity: Activity, signal?: AbortSignal): Promise<RevisionedCommandResponse<CommandResponse>> {
        return axios
            .put(`/api/study-designer/studies/${entity.studyId}/activities/${entity.id}`, entity, { signal })
            .then(response => response.data);
    },
    delete(entity: StudyEntity, signal?: AbortSignal): Promise<RevisionedCommandResponse<CommandResponse>> {
        return axios
            .delete(`/api/study-designer/studies/${entity.studyId}/activities/${entity.id}`, { signal })
            .then(response => response.data);
    },
    move(
        request: ActivityPathParameters & PlacementPayload & { id: string }
    ): Promise<RevisionedCommandResponse<CommandResponse>> {
        return axios
            .post(`/api/study-designer/studies/${request.studyId}/activities/${request.id}/actions/move`, request)
            .then(response => response.data);
    },

    assignConfig(
        request: AssignAmConfigPayload & ActivityPathParameters & { activityId: string }
    ): Promise<RevisionedCommandResponse<CommandResponse>> {
        return axios
            .post(
                `/api/study-designer/studies/${request.studyId}/activities/${request.activityId}/actions/assign-config`,
                request
            )
            .then(response => response.data);
    },
    clone(entity: Activity, signal?: AbortSignal): Promise<RevisionedCommandResponse<EntityCommandCreateResponse>> {
        return axios
            .post(`/api/study-designer/studies/${entity.studyId}/activities/${entity.id}/actions/clone`, entity, {
                signal,
            })
            .then(response => response.data);
    },
    removeTimepoints(
        request: ActivityPathParameters & { activityId: string }
    ): Promise<RevisionedCommandResponse<CommandResponse>> {
        return axios
            .post(
                `/api/study-designer/studies/${request.studyId}/activities/${request.activityId}/actions/remove-timepoints`,
                request
            )
            .then(response => response.data);
    },
};
